import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Constants } from "@/integrations/supabase/types";

const roleLabels: Record<string, string> = {
  superadmin: "Super Admin",
  principal: "Principal",
  deputy_principal: "Deputy Principal",
  head_admission: "Head of Admission",
  head_administration: "Head of Administration",
  head_academic: "Head of Academic",
  head_examination: "Head of Examination",
  head_accounting: "Head of Accounting",
  head_student_service: "Head of Student Services",
  head_discipline: "Head of Discipline",
  hod_cmt: "HoD CMT",
  hod_pst: "HoD PST",
  hod_sw: "HoD SW",
  it_incharge: "IT Incharge",
  security_man: "Security Man",
  cr_cmt4: "CR CMT4",
  cr_cmt5: "CR CMT5",
  cr_cmt6: "CR CMT6",
  cr_pst4: "CR PST4",
  cr_pst5: "CR PST5",
  cr_pst6: "CR PST6",
  cr_sw4: "CR SW4",
  cr_sw5: "CR SW5",
  cr_sw6: "CR SW6",
  auditor: "Auditor",
};

// Exclude "student" — students have a different onboarding flow
const invitableRoles = Constants.public.Enums.app_role.filter(r => r !== "student" && !r.startsWith("cr_"));

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function InviteUserForm({ open, onOpenChange, onSuccess }: Props) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", role: "", salutation: "" });
  const salutationOptions = ["Dr.", "Mr.", "Mrs.", "Ms.", "Prof.", "Rev."];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.role) return;
    setSaving(true);
    try {
      const { error } = await supabase.from("invitations").insert({
        full_name: form.full_name,
        email: form.email.toLowerCase().trim(),
        phone: form.phone || null,
        role: form.role as any,
        invited_by: user!.id,
      });
      if (error) throw error;
      toast({ title: "Invitation created", description: `${form.full_name} can now activate their account.` });
      setForm({ full_name: "", email: "", phone: "", role: "", salutation: "" });
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pre-Register Staff User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Salutation</Label>
            <Select value={form.salutation} onValueChange={v => setForm(f => ({ ...f, salutation: v }))}>
              <SelectTrigger><SelectValue placeholder="Select title" /></SelectTrigger>
              <SelectContent>
                {salutationOptions.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Full Name *</Label>
            <Input value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} required />
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
          </div>
          <div>
            <Label>Mobile Number</Label>
            <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+27..." />
          </div>
          <div>
            <Label>Role *</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
              <SelectContent>
                {invitableRoles.map(r => (
                  <SelectItem key={r} value={r}>{roleLabels[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={saving || !form.role} className="w-full">
            {saving ? "Creating..." : "Create Invitation"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
